import { Geography } from './offer';
import { SearchParameters } from './search';

export enum AutoSearchState {
    OFF = 0,
    ON = 1,
    ERRORED = 2
}

export enum AutoSearchMode {
    CIRCLE_TRIP = 0,
    PREFERRED_DESTINATIONS = 1,
    ANY = 2,
    SELECTED_COUNTRIES = 3
}

export type SearchFilterMode = 'default' | 'preferredRoute' | 'preferredDestinations' | 'returnDestinations' | 'selectedCountries';

/**
 * Maps AutoSearchMode to SearchFilterMode, considering shouldReturnRegCountry flag
 */
export const mapAutoSearchModeToFilterMode = (session: VehicleAutoSearch | null | undefined): SearchFilterMode => {
  if (!session) return 'default';

  switch (session.autoSearchMode) {
    case AutoSearchMode.CIRCLE_TRIP:
      return 'preferredRoute';
    case AutoSearchMode.PREFERRED_DESTINATIONS:
      return session.shouldReturnRegCountry ? 'returnDestinations' : 'preferredDestinations';
    case AutoSearchMode.SELECTED_COUNTRIES:
      return 'selectedCountries';
    case AutoSearchMode.ANY:
    default:
      return 'default';
  }
};

/**
 * Maps SearchFilterMode back to AutoSearchMode and shouldReturnRegCountry flag
 * Inverse of mapAutoSearchModeToFilterMode
 */
export const mapFilterModeToAutoSearchMode = (mode: SearchFilterMode): { auto_search_mode: AutoSearchMode; should_return_reg_country: boolean } => {
  switch (mode) {
    case 'preferredRoute':
      return { auto_search_mode: AutoSearchMode.CIRCLE_TRIP, should_return_reg_country: false };
    case 'preferredDestinations':
      return { auto_search_mode: AutoSearchMode.PREFERRED_DESTINATIONS, should_return_reg_country: false };
    case 'returnDestinations':
      return { auto_search_mode: AutoSearchMode.PREFERRED_DESTINATIONS, should_return_reg_country: true };
    case 'selectedCountries':
      return { auto_search_mode: AutoSearchMode.SELECTED_COUNTRIES, should_return_reg_country: false };
    case 'default':
    default:
      return { auto_search_mode: AutoSearchMode.ANY, should_return_reg_country: false };
  }
};

export interface SearchHistoryEntry {
    recordedAt: Date;
    marketplaceOffersFound: number;
    potentialOffersFound: number;
}

export interface SearchRecord {
    searchedAt: Date;
    marketplace: string;
    searchParameters: SearchParameters;
    mode: string;
    shouldReturnRegCountry?: boolean;
    destinationSource?: string;
    history: SearchHistoryEntry[]; // one entry per execution, append-only
}

export interface AutoSearchHistoryEntry {
    sessions: VehicleAutoSearch[];
    searchHistory: (SearchRecord & {sessionId: number; sessionCreatedAt: Date})[]; // merged & sorted
}

export interface AutoSearchHistoryResponse {
    [vehicleId: number]: AutoSearchHistoryEntry;
}

export interface VehicleAutoSearch {
    id: number;
    vehicle_id: number; // the vehicle for which we search
    messageBodies: { sender: 'MANAGER' | 'AI' | 'TOOL'; body: string; at: number }[];
    autoSearchState: AutoSearchState;
    autoSearchMode: AutoSearchMode;
    shouldReturnRegCountry?: boolean; // only relevant when AutoSearchMode.PREFERRED_DESTINATION.
    selectedCountries?: string[]; // only relevant when AutoSearchMode.SELECTED_COUNTRIES
    fullTruckLoad?: boolean; // whether to filter by minLengthMForFullTruckLoad
    startLoadingTime?: Date; // earliest loading time to search for
    endLoadingTime?: Date; // latest loading time to search for
    user_id?: number; // user who started this search (used to resolve tenantOverrides / effectiveTenant)
    lastTimeSearchedAt?: Date;
    startLocation: Geography;
    endLocation: Geography;
    after_booking_id: number; // if set, only search for offers after this booking
    offers?: string; // JSON array of offer IDs manually picked when by human so we can learn
    searches?: SearchRecord[]; // Array of previous searches done with execution tracking
    statusMessage?: string;
    executions?: Date[]; // Timestamps of when auto-negotiation executions happened
    createdAt: Date;
    toCloseAt: Date;
}

/**
 * Request payload for starting auto search
 */
export interface StartAutoSearchRequest {
    after_booking_id?: number;
    auto_search_mode: AutoSearchMode;
    should_return_reg_country?: boolean;
    selected_countries?: string[];
    full_truck_load?: boolean;
    start_loading_time?: number;
    end_loading_time?: number;
}

/**
 * Request payload for ending auto search
 */
export interface EndAutoSearchRequest {
    cancel_negotiations: boolean;
    send_email: boolean;
    cancel_reason?: number;
}
